import { clsx } from 'clsx';
import {
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactElement,
  type ReactNode,
} from 'react';

type SpecularOwnProps<T extends ElementType> = {
  as?: T;
  children?: ReactNode;
  className?: string;
};

export type SpecularSurfaceProps<T extends ElementType = 'div'> = SpecularOwnProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof SpecularOwnProps<T>>;

/**
 * A picture frame (`overflow: clip`, its own stacking context). It used to be lit like polished stone — a highlight
 * that followed the pointer and a sheen that travelled across; both are retired (D-370, enterprise register: a picture
 * stays still), so no script runs and the wrapper only keeps the frame.
 */
export function SpecularSurface<T extends ElementType = 'div'>({
  as,
  className,
  children,
  ...rest
}: SpecularSurfaceProps<T>): ReactElement {
  const Component: ElementType = as ?? 'div';

  return (
    <Component data-specular="" className={clsx('specular', className)} {...rest}>
      {children}
    </Component>
  );
}
